import { useCallback } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useSelector } from "react-redux";
import { type StringIndexedObject, useForm } from "@kanda-libs/ks-component-ts";
import { useToast } from "@kanda-libs/ks-design-library";
import { actions, Company } from "@kanda-libs/ks-frontend-services";
import companySelectors from "store/selectors/company";
import useReloadInitialData from "hooks/useReloadInitialData";
import { useAppDispatch } from "components/shared/App";

export interface VerifyCompanyFormHook {
  form: UseFormReturn;
  isSubmitting: boolean;
  onSubmit: (formValues: StringIndexedObject) => void;
}

export default function useVerifyCompanyForm(
  handleClose: () => void
): VerifyCompanyFormHook {
  const dispatch = useAppDispatch();
  const form = useForm({ mode: "onBlur" });
  const isSubmitting = useSelector(companySelectors.getIsSubmitting);
  const company = useSelector(companySelectors.getEntity);

  const refreshAll = useReloadInitialData();
  const { showSuccess, showError } = useToast();

  const onSubmit = useCallback(
    (formValues: StringIndexedObject) => {
      // Extract info and check
      const id = company?.id as string;
      const lifecycle = company?.lifecycle;

      if (lifecycle === "registered" || lifecycle === "onboarded") {
        showError("Company has not completed setup");
        return;
      }

      if (lifecycle === "verified") {
        showError("Company is already verified");
        return;
      }

      if (!id || !formValues?.company?.fca_number) return;
      const body = {
        ...company,
        fca_number: formValues?.company?.fca_number,
      };

      const onError = () => {
        handleClose();
        showError("Error approving company");
      };

      dispatch(
        actions.putCompany({
          body: body as Company,
          params: { id },
          onError,
          onSuccess: () => {
            dispatch(
              actions.approveCompany({
                params: { id },
                onError,
                onSuccess: () => {
                  showSuccess("Company approved");
                  handleClose();
                },
              })
            );
          },
        })
      );
    },
    [
      actions.putCompany,
      actions.approveCompany,
      company,
      refreshAll,
      showSuccess,
      handleClose,
    ]
  );

  return {
    form,
    isSubmitting,
    onSubmit,
  };
}
